import { FormatColorText } from '@mui/icons-material'
import HighlightIcon from '@mui/icons-material/Highlight'
import { IconButton, Tooltip, Paper, Box, Typography, TextField, InputAdornment, Popover } from '@mui/material'
import { Editor } from '@tiptap/react'
import { useState } from 'react'
import Helper from '../../../helpers/Helper'
import { useAppSelector, useAppDispatch } from '../../../store/Hooks'
import { addCustomColor, removeCustomColor } from '../../../store/slices/AppSlice'

interface ColorPickerProps {
    editor?: Editor
    color?: string
    onColorChange?: (color: string) => void
    type?: 'text' | 'background'
    tooltip?: string
}

const ColorPicker = ({ editor, color, onColorChange, type = 'text', tooltip }: ColorPickerProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement>(null)
    const [customHex, setCustomHex] = useState('')
    const mode = useAppSelector(state => state.app.mode)
    const customColors = useAppSelector(state => state.app.customColors ?? [])
    const dispatch = useAppDispatch()
    const open = Boolean(anchorEl)

    const mainColors = [
        '#FFFDD0', // Yellow
        '#FFCBE5', // Pink
        '#F4889D', // Red
        '#83FF83', // Green
        '#A8A400', // Olive/Brown
        '#E7E6E6', // Light Gray
        '#AEAAAA', // Dark Gray
        '#000000', // Black
        'reset'
    ]

    const colorsN = [
        '#00555A', // Warm Dark Teal
        '#248E9A', // Warm light Teal
        '#FD660F', // Orange
        '#5BEEE1', // Bright Teal
        '#96FFFB', // Bright Light Teal
        '#F9995A' // Light Orange
    ]

    const getDefaultColor = () => {
        if (type === 'background') {
            return 'transparent'
        }
        return mode === 'dark' ? '#FFFFFF' : '#000000'
    }

    const getCurrentColor = () => {
        if (editor) {
            if (type === 'background') {
                const highlightColor = editor.getAttributes('highlight').color
                return highlightColor ? Helper.rgbToHex(highlightColor) : 'transparent'
            }
            const rawColor = editor.getAttributes('textStyle').color ?? getDefaultColor()
            return Helper.rgbToHex(rawColor)
        }
        return color
    }

    const currentColor = getCurrentColor()
    const iconSize = '30px'

    const getTooltipText = () => {
        if (tooltip) return tooltip
        return type === 'background' ? 'Background Color' : 'Text Color'
    }

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleColorSelect = (color: string) => {
        if (color === 'reset') {
            handleColorReset()
            return
        }

        if (editor) {
            if (type === 'background') {
                editor.chain().focus().setHighlight({ color }).run()
            } else {
                editor.chain().focus().setColor(color).run()
            }
        } else if (onColorChange) {
            onColorChange(color)
        }
        handleClose()
    }

    const handleColorReset = () => {
        if (editor) {
            if (type === 'background') {
                editor.chain().focus().unsetHighlight().run()
            } else {
                editor.chain().focus().unsetColor().run()
            }
        } else if (onColorChange) {
            onColorChange(undefined)
        }
        handleClose()
    }

    const handleCustomHexChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomHex(event.target.value)
    }

    const applyCustomHex = () => {
        let hexColor = customHex.trim()
        if (!hexColor.startsWith('#')) {
            hexColor = `#${hexColor}`
        }

        if (/^#[0-9A-F]{6}$/i.test(hexColor)) {
            dispatch(addCustomColor(hexColor))
            handleColorSelect(hexColor)
            setCustomHex('')
        }
    }

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            applyCustomHex()
        }
    }

    const handleRemoveCustomColor = (colorToRemove: string) => {
        dispatch(removeCustomColor(colorToRemove))
    }

    const renderMainColorButton = (color: string, isReset = false) => {
        return (
            <Box
                key={color}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                <IconButton
                    size='small'
                    onClick={() => handleColorSelect(color)}
                    sx={{
                        'width': 35,
                        'height': 19,
                        'backgroundColor': isReset ? 'background.paper' : color,
                        'border': currentColor === color ? 3 : 2,
                        'borderColor': currentColor === color ? 'primary.main' : '#000',
                        'borderStyle': 'solid',
                        'borderRadius': 1.5,
                        'p': 0,
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: isReset ? 'background.paper' : color
                        }
                    }}>
                    {isReset && (
                        <Typography
                            sx={{
                                fontSize: 18,
                                fontWeight: 'bold',
                                color: '#FF3F64'
                            }}>
                            ×
                        </Typography>
                    )}
                </IconButton>
            </Box>
        )
    }

    const renderCustomColorButton = (color: string, index: number) => {
        const isEmpty = !color

        return (
            <Box
                key={`custom-${index}`}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                <IconButton
                    size='small'
                    onClick={() => !isEmpty && handleColorSelect(color)}
                    disabled={isEmpty}
                    sx={{
                        'width': 25,
                        'height': 25,
                        'backgroundColor': isEmpty ? 'transparent' : color,
                        'border': isEmpty ? 2 : (currentColor === color ? 3 : 2),
                        'borderColor': isEmpty ? '#ccc' : (currentColor === color ? 'primary.main' : '#000'),
                        'borderStyle': isEmpty ? 'dashed' : 'solid',
                        'borderRadius': 1,
                        'p': 0,
                        '&:hover': !isEmpty
                            ? {
                                    borderColor: 'primary.main',
                                    backgroundColor: isEmpty ? 'transparent' : color
                                }
                            : {},
                        '&:disabled': {
                            backgroundColor: 'transparent'
                        }
                    }}
                />

                {!isEmpty && (
                    <IconButton
                        size='small'
                        onClick={e => {
                            e.stopPropagation()
                            handleRemoveCustomColor(color)
                        }}
                        sx={{
                            'position': 'absolute',
                            'top': -6,
                            'right': -6,
                            'width': 16,
                            'height': 16,
                            'backgroundColor': '#FF3F64',
                            'color': 'white',
                            'fontSize': 10,
                            'minWidth': 16,
                            '&:hover': {
                                backgroundColor: '#FF1744'
                            }
                        }}>
                        ×
                    </IconButton>
                )}
            </Box>
        )
    }

    return (
        <>
            <Tooltip title={getTooltipText()} placement='top'>
                <IconButton
                    key={currentColor}
                    size='small'
                    onClick={handleOpen}
                    sx={{
                        'height': iconSize,
                        'width': iconSize,
                        'color': currentColor === 'transparent' ? 'text.primary' : currentColor,
                        'border': 1,
                        'borderColor': 'divider',
                        'borderRadius': 1,
                        '&:hover': {
                            backgroundColor: 'action.hover'
                        }
                    }}>
                    {type === 'background' ? <HighlightIcon /> : <FormatColorText />}
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}>
                <Paper sx={{ p: 2, width: 160 }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 1,
                            mb: 2,
                            justifyItems: 'center'
                        }}>
                        {mainColors.map((color, index) =>
                            index === 8 ? renderMainColorButton(color, true) : renderMainColorButton(color)
                        )}
                    </Box>

                    <Typography
                        variant='body2'
                        sx={{
                            mb: 1,
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: 10,
                            textAlign: 'start'
                        }}>
                        NASCO colors
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 1,
                            mb: 2,
                            justifyItems: 'center'
                        }}>
                        {colorsN.map(color => renderMainColorButton(color))}
                    </Box>

                    <Typography
                        variant='body2'
                        sx={{
                            mb: 1,
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: 10,
                            textAlign: 'start'
                        }}>
                        Custom color HEX
                    </Typography>

                    <TextField
                        size='small'
                        value={customHex}
                        onChange={handleCustomHexChange}
                        onKeyPress={handleKeyPress}
                        placeholder='00000'
                        sx={{
                            'mb': 1,
                            'width': '100%',
                            '& .MuiInputBase-root': {
                                height: 32
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <Typography variant='body2' sx={{ fontSize: 12 }}>#</Typography>
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <IconButton
                                        size='small'
                                        onClick={applyCustomHex}
                                        sx={{ p: 0.5, fontSize: 12 }}>
                                        ↵
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            mt: 1,
                            justifyContent: 'space-between'
                        }}>
                        {[0, 1, 2, 3].map(index => {
                            const customColor = customColors[index] ?? ''
                            return renderCustomColorButton(customColor, index)
                        })}
                    </Box>
                </Paper>
            </Popover>
        </>
    )
}

export default ColorPicker
